import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { IPermission } from '../../../../data/types';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ErrorText } from '../../../shared/form/ErrorMessage';

export interface IOrganizationSelectionProps {
  organizations: IPermission[];
  selectedOrganization: string | null;
  onSelectOrganization: (orgId: string | null) => void;
  error?: string;
}

export const OrganizationSelection = React.memo(
  (props: IOrganizationSelectionProps): React.ReactElement => {
    const handleOrganizationClick = React.useCallback(
      (orgId: string): void => {
        if (props.selectedOrganization === orgId) {
          props.onSelectOrganization(null);
        } else {
          props.onSelectOrganization(orgId);
        }
      },
      [props.selectedOrganization, props.onSelectOrganization]
    );

    const errorId = 'organization-error';

    return (
      <React.Fragment>
        <h1 id='organization-heading' style={{ margin: '.5rem 1rem' }}>
          Select Organization
        </h1>
        <p
          id='organization-description'
          style={{ margin: '0 1rem', color: '#888', fontSize: '0.9rem' }}
        >
          Your account has access to multiple organizations. Please select which
          one you'd like to sign in to.
        </p>
        <InputContainer
          role='form'
          aria-labelledby='organization-heading'
          aria-describedby='organization-description'
        >
          <ControlSpacer style={{ gap: '10px' }} role='radiogroup'>
            {props.organizations.map(
              (org: IPermission, index: number): React.ReactElement => {
                const isSelected = props.selectedOrganization === org.orgId;
                const displayName = org.teamName || org.orgId;

                return (
                  <OrganizationCard
                    key={org.orgId}
                    selected={isSelected}
                    onClick={(): void => handleOrganizationClick(org.orgId)}
                    animationDelay={index}
                    role='radio'
                    aria-checked={isSelected}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent): void => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOrganizationClick(org.orgId);
                      }
                    }}
                  >
                    <OrganizationName>{displayName}</OrganizationName>
                    <OrganizationRole>
                      {org.role ? org.role : org.write ? 'Admin' : 'Member'}
                    </OrganizationRole>
                  </OrganizationCard>
                );
              }
            )}
          </ControlSpacer>
          {props.error && (
            <ErrorText id={errorId} role='alert' aria-live='polite'>
              {props.error}
            </ErrorText>
          )}
        </InputContainer>
      </React.Fragment>
    );
  },
  (
    prevProps: Readonly<IOrganizationSelectionProps>,
    nextProps: Readonly<IOrganizationSelectionProps>
  ): boolean => {
    return (
      prevProps.organizations === nextProps.organizations &&
      prevProps.selectedOrganization === nextProps.selectedOrganization &&
      prevProps.error === nextProps.error &&
      prevProps.onSelectOrganization === nextProps.onSelectOrganization
    );
  }
);

OrganizationSelection.displayName = 'OrganizationSelection';

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  padding-top: 10px;
  width: 100%;
  height: 100%;
`;

const OrganizationCard = styled.div<{ selected: boolean; animationDelay: number }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 2px solid ${(props): string => (props.selected ? '#0066cc' : '#ddd')};
  border-radius: 8px;
  background-color: ${(props): string => (props.selected ? '#f0f7ff' : '#fff')};
  cursor: pointer;
  transition: all 0.2s ease;
  animation: ${fadeSlideIn} 400ms ease-out;
  animation-delay: ${(props): string => `${props.animationDelay * 80}ms`};
  animation-fill-mode: both;

  &:hover {
    border-color: #0066cc;
    background-color: #f0f7ff;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 102, 204, 0.1);
  }

  &:focus {
    outline: 2px solid #0066cc;
    outline-offset: 2px;
  }
`;

const OrganizationName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const OrganizationRole = styled.div`
  font-size: 0.85rem;
  color: #666;
`;
